import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "react-toastify";
import S3Service from "../services/s3Service";
import Loader from "../components/Loader";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { parse, format, isValid } from "date-fns";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";

// Helper function to get S3 URL
const getS3Url = (key) => {
  if (!key) return null;
  return S3Service.getFileUrl(key);
};

// Accept common YouTube URL formats: watch?v=, youtu.be/, embed/, shorts/, v/
const isValidYouTubeUrl = (value) => {
  if (!value || !value.trim()) return true; // empty is allowed (optional field)
  const url = value.trim();
  const patterns = [
    /^(https?:\/\/)?(www\.)?(youtube\.com|youtube-nocookie\.com)\/embed\/[\w-]{6,}/i,
    /^(https?:\/\/)?youtu\.be\/[\w-]{6,}/i,
    /^(https?:\/\/)?(www\.)?(youtube\.com|youtube-nocookie\.com)\/shorts\/[\w-]{6,}/i,
    /^(https?:\/\/)?(www\.)?(youtube\.com|youtube-nocookie\.com)\/v\/[\w-]{6,}/i,
    /^(https?:\/\/)?(www\.)?(youtube\.com|youtube-nocookie\.com)\/watch\?[^#]*\bv=[\w-]{6,}/i,
  ];
  return patterns.some((pattern) => pattern.test(url));
};

// Helper functions for UK date format (DD/MM/YYYY) - matching Laravel
const parseDate = (dateString) => {
  if (!dateString) return null;
  // Handle YYYY-MM-DD format from backend
  if (/^\d{4}-\d{2}-\d{2}/.test(dateString)) {
    const date = new Date(dateString);
    return isValid(date) ? date : null;
  }
  // Handle DD/MM/YYYY format
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateString)) {
    const parsed = parse(dateString, "dd/MM/yyyy", new Date());
    return isValid(parsed) ? parsed : null;
  }
  return null;
};

const formatDateForDisplay = (date) => {
  if (!date || !isValid(date)) return "";
  return format(date, "dd/MM/yyyy");
};

const formatDateForBackend = (date) => {
  if (!date || !isValid(date)) return "";
  return format(date, "yyyy-MM-dd");
};

// Helper function to strip HTML tags for plain text display
const stripHtml = (html) => {
  if (!html) return "";
  // Create a temporary element to parse HTML
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
};

// import RichTextEditor from '../components/RichTextEditor'; // Uncomment if you have a rich text editor

const liabilitiesList = [
  "AOV/Fire doors/keys",
  "Address management",
  "Asbestos management",
  "Boilers",
  "Consultation",
  "Council tax",
  "Fire risk",
  "Fire safety signage/evac planning",
  "Fridge magnets",
  "Gas safety",
  "Legionella",
  "PAT",
  "Post Redirection/Forwarding",
  "Pre-handover cleaning",
  "Pre-handover inventory",
  "Pre-handover maintenance",
  "Pre-handover works",
  "Pre-handover/cleaning",
  "Pre-handover/keys",
  "Pre-handover/maintenance",
  "Pre-handover/works",
  "Pre-handover/other",
  "Utilities",
  "Void works",
  "Water hygiene",
  "Miscellaneous",
  "Other",
];

const staticDropdown = (options) =>
  (Array.isArray(options) ? options : []).map((opt) => (
    <option key={opt} value={opt}>
      {opt}
    </option>
  ));

const PropertyEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission, user } = useAuth();

  const [form, setForm] = useState({
    name: "",
    property_id: "",
    status: "",
    status_manual: "",
    residential_commercial: "",
    client_id: "",
    client_contact_id: "",
    building_manager_id: "",
    block_id: "",
    inspection_route_id: "",
    type_id: "",
    location_id: "",
    address_1: "",
    address_2: "",
    address_3: "",
    town: "",
    postcode: "",
    country: "",
    county: "",
    geo_latitude: "",
    geo_longitude: "",
    price_from: "",
    price_to: "",
    actual_market_rent: "",
    square_footage: "",
    rateable_value: "",
    rebuild_value: "",
    spaces: "",
    bathrooms: "",
    kitchens: "",
    garden: "",
    parking: "",
    vip_rule_type: "",
    vip_amount: "",
    vip_provided: "",
    vip_amount_rule: "",
    vip_date_to_pay: "",
    total_vip_paid: "",
    savings_made: "",
    date_taken_on: "",
    date_taken_back: "",
    date_live: "",
    date_notice: "",
    date_feature_from: "",
    date_feature_to: "",
    short_description: "",
    description: "",
    fit_out_notes: "",
    utilities_notes: "",
    public_description: "",
    public_short_description: "",
    website_display_name: "",
    video: "",
    mapping: "",
    main_photo: null,
    photos: [],
    monthly_inspection_status: "",
    client_type: "",
    liability: {},
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    clients: {},
    clientTypes: {},
    locations: {},
    types: {},
    buildingManagers: {},
    manualStatuses: {},
  });
  const [clients, setClients] = useState([]); // State for clients
  const [clientContacts, setClientContacts] = useState([]); // State for client contacts
  const [propertyTypes, setPropertyTypes] = useState([]); // State for property types
  const [locations, setLocations] = useState([]); // State for locations
  const [buildingManagers, setBuildingManagers] = useState([]); // State for building managers
  const [validationErrors, setValidationErrors] = useState({});
  const [uploadProgress, setUploadProgress] = useState({}); // Track upload progress
  const [uploadingFiles, setUploadingFiles] = useState(false); // Track if files are uploading
  const [generatingGeoCode, setGeneratingGeoCode] = useState(false); // Track geo code generation
  const [vipRules, setVipRules] = useState([]); // Dynamic VIP rules for unique VIP rule type

  useEffect(() => {
    setLoading(true);
    console.log(`🔍 [FRONTEND] Fetching property with ID: ${id}`);

    fetch(`${process.env.REACT_APP_API_URL}/api/properties/${id}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    })
      .then((res) => {
        console.log(`🔍 [FRONTEND] API Response status: ${res.status}`);
        if (res.ok) {
          return res.json();
        } else {
          return res.json().then((err) => {
            throw new Error(err.message || `HTTP ${res.status}`);
          });
        }
      })
      .then((data) => {
        console.log(`🔍 [FRONTEND] API Response data:`, data);

        // Extract property data from the nested structure
        const property = data.data?.property || data.property || {};
        console.log(`🔍 [FRONTEND] Extracted property data:`, property);

        // Map the property data to form fields
        const mappedForm = {
          ...form,
          name: property.name || "",
          property_id: property.property_id || "",
          status: property.status || "",
          // Convert status_manual to string for dropdown matching (backend might return number)
          status_manual: property.status_manual !== null && property.status_manual !== undefined 
            ? String(property.status_manual) 
            : "",
          residential_commercial: property.residential_commercial || "",
          client_id: property.client_id || "",
          client_contact_id: property.client_contact_id || "",
          building_manager_id: property.building_manager_id || "",
          block_id: property.block_id || "",
          inspection_route_id: property.inspection_route_id || "",
          type_id: property.type_id || "",
          location_id: property.location_id || "",
          address_1: property.address_1 || "",
          address_2: property.address_2 || "",
          address_3: property.address_3 || "",
          town: property.town || "",
          postcode: property.postcode || "",
          country: property.country || "",
          county: property.county || "",
          geo_latitude: property.geo_latitude || "",
          geo_longitude: property.geo_longitude || "",
          price_from: property.price_from || "",
          price_to: property.price_to || "",
          actual_market_rent: property.actual_market_rent || "",
          square_footage: property.square_footage || "",
          rateable_value: property.rateable_value || "",
          rebuild_value: property.rebuild_value || "",
          spaces: property.spaces || "",
          bathrooms: property.bathrooms || "",
          kitchens: property.kitchens || "",
          // Convert 0/1 to "No"/"Yes" for display (Laravel defaults to 0 = "No")
          garden: property.garden === 0 || property.garden === "0" ? "No" : property.garden === 1 || property.garden === "1" ? "Yes" : "No",
          parking: property.parking === 0 || property.parking === "0" ? "No" : property.parking === 1 || property.parking === "1" ? "Yes" : "No",
          vip_rule_type: property.vip_rule_type || "",
          vip_amount: property.vip_amount || "",
          vip_provided: property.vip_provided || "",
          vip_amount_rule: property.vip_amount_rule || "",
          // Parse VIP rules if they exist
          vipRules: (() => {
            try {
              if (property.vip_amount_rule) {
                const parsed = typeof property.vip_amount_rule === 'string' 
                  ? JSON.parse(property.vip_amount_rule) 
                  : property.vip_amount_rule;
                return Array.isArray(parsed) ? parsed : [];
              }
              return [];
            } catch (e) {
              console.error("Error parsing VIP rules:", e);
              return [];
            }
          })(),
          vip_date_to_pay: property.vip_date_to_pay || "",
          total_vip_paid: property.total_vip_paid || "",
          savings_made: property.savings_made || "",
          date_taken_on: property.date_taken_on || "",
          date_taken_back: property.date_taken_back || "",
          date_live: property.date_live || "",
          date_notice: property.date_notice || "",
          date_feature_from: property.date_feature_from || "",
          date_feature_to: property.date_feature_to || "",
          short_description: stripHtml(property.short_description) || "",
          description: stripHtml(property.description) || "",
          fit_out_notes: stripHtml(property.fit_out_notes) || "",
          utilities_notes: stripHtml(property.utilities_notes) || "",
          public_description: property.public_description || "",
          public_short_description: stripHtml(property.public_short_description) || "",
          website_display_name: property.website_display_name || "",
          video: property.video || "",
          mapping: property["3d_mapping"] || "",
          main_photo: property.main_photo || null,
          photos: property.photos || [],
          monthly_inspection_status: property.monthly_inspection_status || "",
          client_type: property.client_type || "",
          liability: property.liability || {},
        };

        console.log(`🔍 [FRONTEND] Mapped form data:`, mappedForm);
        console.log(`🔍 [FRONTEND] Form data after mapping:`, {
          name: mappedForm.name,
          property_id: mappedForm.property_id,
          status_manual: mappedForm.status_manual,
          client_id: mappedForm.client_id,
          spaces: mappedForm.spaces,
          bathrooms: mappedForm.bathrooms,
          kitchens: mappedForm.kitchens,
        });
        setForm(mappedForm);
        // Set VIP rules separately for dynamic builder
        const initialVipRules = mappedForm.vipRules || [];
        setVipRules(initialVipRules);
        
        // If VIP rule type is 2 (unique) and no rules exist, initialize with one empty rule
        if ((mappedForm.vip_rule_type === "2" || mappedForm.vip_rule_type === 2) && initialVipRules.length === 0) {
          setVipRules([{ months: "Onwards", amount: "" }]);
        }
        
        // Fetch client contacts for the existing client_id if it exists
        if (mappedForm.client_id) {
          console.log(`🔍 [PropertyEdit] Fetching client contacts for existing client_id: ${mappedForm.client_id}`);
          fetchClientContacts(mappedForm.client_id);
        }
        
        setLoading(false);
      })
      .catch((err) => {
        console.error(`❌ [FRONTEND] Error loading property:`, err);
        setError(err.message || "Failed to load property");
        setLoading(false);
      });
  }, [id]);

  useEffect(() => {
    // Fetch clients and client contacts
    fetch(`${process.env.REACT_APP_API_URL}/api/clients`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    })
      .then((res) =>
        res.ok
          ? res.json()
          : res.json().then((err) => {
              throw new Error(err.message);
            })
      )
      .then((data) => setClients(data.clients))
      .catch((err) => setError(err.message || "Failed to load clients"));

    // Don't fetch all client contacts here - fetch them dynamically when client_id is selected
    // This prevents overwriting filtered contacts that are fetched when property loads
    // Client contacts are now fetched:
    // 1. When property loads with existing client_id (line 261)
    // 2. When user changes client_id (line 445)

    // Fetch property types and locations
    fetch(`${process.env.REACT_APP_API_URL}/api/property-types`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    })
      .then((res) =>
        res.ok
          ? res.json()
          : res.json().then((err) => {
              throw new Error(err.message);
            })
      )
      .then((data) => setPropertyTypes(data.property_types))
      .catch((err) => setError(err.message || "Failed to load property types"));

    fetch(`${process.env.REACT_APP_API_URL}/api/locations`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    })
      .then((res) =>
        res.ok
          ? res.json()
          : res.json().then((err) => {
              throw new Error(err.message);
            })
      )
      .then((data) => setLocations(data.locations))
      .catch((err) => setError(err.message || "Failed to load locations"));

    // Fetch building managers
    fetch(`${process.env.REACT_APP_API_URL}/api/users/building-managers`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    })
      .then((res) =>
        res.ok
          ? res.json()
          : res.json().then((err) => {
              throw new Error(err.message);
            })
      )
      .then((data) => setBuildingManagers(data.users))
      .catch((err) =>
        setError(err.message || "Failed to load building managers")
      );
  }, []); // Empty dependency array to run only once on mount

  // Fetch form data for dropdowns
  useEffect(() => {
    // Only fetch form data if we're not loading property data
    if (loading) {
      console.log("🔄 Form data fetch skipped - still loading property data");
      return;
    }

    const fetchFormData = async () => {
      try {
        console.log("🔄 Fetching form data for dropdowns...");
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/properties/create`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        if (!response.ok) {
          const errorText = await response.text();
          console.error("Form data API Error Response:", errorText);
          throw new Error(
            `Failed to fetch form data: ${response.status} ${response.statusText}`
          );
        }

        const data = await response.json();
        console.log("📋 Form data API response:", data);

        if (data.success && data.data) {
          console.log("✅ Setting form data:", data.data);
          console.log("📊 manualStatuses:", data.data.manualStatuses);
          console.log("📊 clients:", data.data.clients);
          console.log("📊 clientTypes:", data.data.clientTypes);
          setFormData(data.data);
        } else {
          console.error("Form data response missing success or data:", data);
          throw new Error(data.message || "Invalid form data response");
        }
      } catch (error) {
        console.error("Error fetching form data:", error);
        setError(`Failed to load form data: ${error.message}`);
      }
    };

    fetchFormData();
  }, [loading]);

  // Also fetch form data immediately on mount
  useEffect(() => {
    const fetchFormDataImmediately = async () => {
      try {
        console.log("🔄 Fetching form data immediately on mount...");
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/properties/create`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        if (!response.ok) {
          const errorText = await response.text();
          console.error("Immediate form data API Error Response:", errorText);
          return;
        }

        const data = await response.json();
        console.log("📋 Immediate form data API response:", data);

        if (data.success && data.data) {
          console.log("✅ Setting immediate form data:", data.data);
          console.log("📊 Immediate manualStatuses:", data.data.manualStatuses);
          setFormData(data.data);
        }
      } catch (error) {
        console.error("Error fetching form data immediately:", error);
      }
    };

    fetchFormDataImmediately();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;
    console.log(`🔄 Field change: ${name} = ${value}`);

    if (type === "file") {
      handleFileUpload(name, files);
    } else {
      setForm({ ...form, [name]: value });

      // If client changes, fetch client contacts
      if (name === "client_id") {
        if (value) {
          fetchClientContacts(value);
        } else {
          // Clear client contacts when client is cleared
          setClientContacts([]);
        }
        // Clear client contact when client changes
        setForm((prev) => ({ ...prev, client_contact_id: "" }));
      }

      // If VIP rule type changes to unique (2), ensure vipRules array exists
      if (name === "vip_rule_type" && value === "2" && vipRules.length === 0) {
        setVipRules([{ months: "Onwards", amount: "" }]);
      }
    }
  };

  // VIP Rules handlers
  const handleAddVipRule = () => {
    setVipRules([...vipRules, { months: "Onwards", amount: "" }]);
  };

  const handleRemoveVipRule = (index) => {
    const updatedRules = vipRules.filter((_, i) => i !== index);
    setVipRules(updatedRules);

    // Clear the VIP amount validation error once all remaining rules have an amount
    if (
      validationErrors.vip_amount_rule &&
      updatedRules.length > 0 &&
      updatedRules.every((rule) => rule.amount && rule.amount.trim() !== "")
    ) {
      setValidationErrors((prev) => {
        const { vip_amount_rule, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleVipRuleChange = (index, field, value) => {
    const updatedRules = [...vipRules];
    updatedRules[index] = { ...updatedRules[index], [field]: value };
    setVipRules(updatedRules);

    // Clear the VIP amount validation error once all rules have an amount
    if (
      validationErrors.vip_amount_rule &&
      updatedRules.length > 0 &&
      updatedRules.every((rule) => rule.amount && rule.amount.trim() !== "")
    ) {
      setValidationErrors((prev) => {
        const { vip_amount_rule, ...rest } = prev;
        return rest;
      });
    }
  };

  // Radios can't normally be unchecked; clicking the already-selected VIP rule
  // clears the selection (field is optional).
  const handleVipRuleTypeClick = (value) => {
    if (String(form.vip_rule_type) === String(value)) {
      setForm((prev) => ({ ...prev, vip_rule_type: "" }));
      if (validationErrors.vip_amount_rule) {
        setValidationErrors((prev) => {
          const { vip_amount_rule, ...rest } = prev;
          return rest;
        });
      }
    }
  };

  const fetchClientContacts = async (clientId) => {
    try {
      console.log(`🔍 [PropertyEdit] Fetching client contacts for client_id: ${clientId}`);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/client-contacts?client_id=${clientId}`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch client contacts");
      }

      const data = await response.json();
      console.log(`✅ [PropertyEdit] Client contacts API response:`, data);
      
      // API returns { success: true, data: contacts[], message: "..." }
      // Extract contacts from data.data (not data.client_contacts)
      const contacts = data.data || data.client_contacts || [];
      console.log(`✅ [PropertyEdit] Found ${contacts.length} client contacts`);
      setClientContacts(contacts);
    } catch (error) {
      console.error("❌ [PropertyEdit] Error fetching client contacts:", error);
      setClientContacts([]); // Clear contacts on error
    }
  };

  const handleFileUpload = async (fieldName, files) => {
    if (!files || files.length === 0) return;

    setUploadingFiles(true);
    const s3Service = new S3Service();
    const uploadedKeys = [];

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const fileId = `${fieldName}_${i}`;

        // Update progress
        setUploadProgress((prev) => ({
          ...prev,
          [fileId]: 0,
        }));

        // Upload to S3
        const key = await s3Service.uploadImage(file, {
          folder: "properties",
          onProgress: (progress) => {
            setUploadProgress((prev) => ({
              ...prev,
              [fileId]: progress,
            }));
          },
        });

        uploadedKeys.push(key);

        // Update progress to 100%
        setUploadProgress((prev) => ({
          ...prev,
          [fileId]: 100,
        }));
      }

      // Update form with S3 keys
      setForm((prev) => ({
        ...prev,
        [fieldName]: fieldName === "photos" ? uploadedKeys : uploadedKeys[0],
      }));
    } catch (error) {
      console.error("File upload error:", error);
      setError(`Failed to upload ${fieldName}: ${error.message}`);
    } finally {
      setUploadingFiles(false);
      // Clear progress after a delay
      setTimeout(() => {
        setUploadProgress({});
      }, 2000);
    }
  };

  const removeMainPhoto = () => {
    setForm((prev) => ({
      ...prev,
      main_photo: null,
    }));
  };

  const handleGenerateGeoCode = async () => {
    if (!form.postcode) {
      toast.error("There is no postcode!");
      return;
    }

    setGeneratingGeoCode(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/external/generate-geo-codes-from-postcode`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`
          },
          body: JSON.stringify({
            postcode: form.postcode
          })
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch geo codes");
      }

      const data = await response.json();

      if (data.result && data.result.latitude && data.result.longitude) {
        setForm((prev) => ({
          ...prev,
          geo_latitude: data.result.latitude,
          geo_longitude: data.result.longitude,
        }));
        toast.success("Geo codes generated successfully!");
      } else {
        throw new Error("Failed to generate geo codes");
      }
    } catch (error) {
      console.error("Error generating geo codes:", error);
      toast.error(`Failed to generate geo codes: ${error.message}`);
    } finally {
      setGeneratingGeoCode(false);
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!form.name) errors.name = "Property Name is required";
    if (!form.property_id) errors.property_id = "Property ID is required";
    if (!form.status_manual || form.status_manual === "") {
      errors.status_manual = "Status is required";
    }
    if (!form.residential_commercial) {
      errors.residential_commercial = "Residential/Commercial is required";
    }
    if (!form.client_id || isNaN(Number(form.client_id)))
      errors.client_id = "Client is required and must be a number";
    if (
      !form.price_from ||
      isNaN(Number(form.price_from)) ||
      Number(form.price_from) < 0
    )
      errors.price_from =
        "Price From is required and must be zero or a positive number";
    if (
      !form.price_to ||
      isNaN(Number(form.price_to)) ||
      Number(form.price_to) < 0
    )
      errors.price_to =
        "Price To is required and must be zero or a positive number";
    if (
      !form.actual_market_rent ||
      isNaN(Number(form.actual_market_rent)) ||
      Number(form.actual_market_rent) < 0
    )
      errors.actual_market_rent =
        "Actual Market Rent is required and must be zero or a positive number";
    if (
      form.square_footage &&
      (isNaN(Number(form.square_footage)) || Number(form.square_footage) < 0)
    )
      errors.square_footage =
        "Square Footage must be zero or a positive number";
    if (
      form.rateable_value &&
      (isNaN(Number(form.rateable_value)) || Number(form.rateable_value) < 0)
    )
      errors.rateable_value =
        "Rateable Value must be zero or a positive number";
    if (
      form.rebuild_value &&
      (isNaN(Number(form.rebuild_value)) || Number(form.rebuild_value) < 0)
    )
      errors.rebuild_value = "Rebuild Value must be zero or a positive number";
    if (form.location_id && isNaN(Number(form.location_id)))
      errors.location_id = "Location must be a number";
    if (form.type_id && isNaN(Number(form.type_id)))
      errors.type_id = "Type must be a number";
    if (!form.spaces || isNaN(Number(form.spaces)) || Number(form.spaces) < 0)
      errors.spaces =
        "Spaces is required and must be zero or a positive number";
    if (
      !form.bathrooms ||
      isNaN(Number(form.bathrooms)) ||
      Number(form.bathrooms) < 0
    )
      errors.bathrooms =
        "Bathrooms is required and must be zero or a positive number";
    if (
      !form.kitchens ||
      isNaN(Number(form.kitchens)) ||
      Number(form.kitchens) < 0
    )
      errors.kitchens =
        "Kitchens is required and must be zero or a positive number";
    if (form.name && form.name.length > 100)
      errors.name = "Property Name must be at most 100 characters";
    if (form.property_id && form.property_id.length > 30)
      errors.property_id = "Property ID must be at most 30 characters";
    // status_manual: enum check (in Laravel, from config)
    const validStatuses =
      Object.keys(formData.manualStatuses || {}).length > 0
        ? Object.keys(formData.manualStatuses || {})
        : ["999", "9", "0", "4", "5", "6", "11", "14"]; // Fallback to backend values

    if (form.status_manual && !validStatuses.includes(form.status_manual)) {
      errors.status_manual = "Status Manual is invalid";
    }
    if (form.short_description && form.short_description.length > 32768)
      errors.short_description =
        "Short Description must be at most 32768 characters";
    if (form.address_1 && form.address_1.length > 255)
      errors.address_1 = "Address 1 must be at most 255 characters";
    if (form.address_2 && form.address_2.length > 255)
      errors.address_2 = "Address 2 must be at most 255 characters";
    if (form.address_3 && form.address_3.length > 255)
      errors.address_3 = "Address 3 must be at most 255 characters";
    if (form.town && form.town.length > 255)
      errors.town = "Town must be at most 255 characters";
    if (form.postcode && form.postcode.length > 255)
      errors.postcode = "Postcode must be at most 255 characters";
    if (form.video && !isValidYouTubeUrl(form.video))
      errors.video = "Video must be a valid YouTube URL (e.g. https://www.youtube.com/watch?v=...)";
    if (form.video && form.video.length > 255)
      errors.video = "Video URL must be at most 255 characters";
    // Garden / Parking: accept Yes/No or 0/1
    if (
      form.garden !== undefined &&
      form.garden !== null &&
      form.garden !== "" &&
      !["Yes", "No", "0", "1"].includes(String(form.garden))
    )
      errors.garden = "Garden must be Yes or No";
    if (
      form.parking !== undefined &&
      form.parking !== null &&
      form.parking !== "" &&
      !["Yes", "No", "0", "1"].includes(String(form.parking))
    )
      errors.parking = "Parking must be Yes or No";
    if (
      form.vip_rule_type !== undefined &&
      form.vip_rule_type !== null &&
      form.vip_rule_type !== "" &&
      !["0", "1", "2"].includes(String(form.vip_rule_type))
    )
      errors.vip_rule_type = "VIP Rule Type must be 0, 1, or 2";
    if (form.client_type && isNaN(Number(form.client_type)))
      errors.client_type = "Client Type must be a number";
    // Date fields: HTML input uses YYYY-MM-DD; accept that
    // Only validate format if the field has a non-empty value
    const dateIsoRegex = /^\d{4}-\d{2}-\d{2}$/;
    const isValidDateValue = (dateValue) => {
      // Return true if empty (null, undefined, empty string, or whitespace only)
      if (!dateValue || (typeof dateValue === 'string' && dateValue.trim() === '')) {
        return true; // Empty is valid (optional fields)
      }
      
      const dateStr = String(dateValue).trim();
      
      // Check if it's already in YYYY-MM-DD format
      if (dateIsoRegex.test(dateStr)) {
        return true;
      }
      
      // Try to parse ISO date strings (e.g., "2019-12-12T00:00:00.000Z") and convert to YYYY-MM-DD
      try {
        const date = new Date(dateStr);
        if (!isNaN(date.getTime())) {
          // Valid date - check if it's already in YYYY-MM-DD format or can be converted
          // If the string contains 'T' or 'Z', it's an ISO string - this is valid for date inputs
          // HTML5 date inputs accept ISO strings and convert them automatically
          if (dateStr.includes('T') || dateStr.includes('Z')) {
            return true; // ISO date strings are valid for HTML5 date inputs
          }
          // Otherwise, check if it matches YYYY-MM-DD
          return dateIsoRegex.test(dateStr);
        }
      } catch (e) {
        // Invalid date string
      }
      
      // If has value, must match YYYY-MM-DD format
      return dateIsoRegex.test(dateStr);
    };
    
    const isDateFieldEmpty = (dateValue) => {
      return !dateValue || 
             (typeof dateValue === 'string' && dateValue.trim() === '') ||
             dateValue === null ||
             dateValue === undefined;
    };
    
    // Only validate if field has a value (not empty)
    if (!isDateFieldEmpty(form.date_taken_on) && !isValidDateValue(form.date_taken_on))
      errors.date_taken_on = "Date Taken On must be in YYYY-MM-DD format";
    
    // Check both date_live and date_Live (handle case sensitivity)
    const dateLive = form.date_live || form.date_Live;
    if (!isDateFieldEmpty(dateLive) && !isValidDateValue(dateLive))
      errors.date_live = "Date Live must be in YYYY-MM-DD format";
    
    if (!isDateFieldEmpty(form.date_taken_back) && !isValidDateValue(form.date_taken_back))
      errors.date_taken_back = "Date Taken Back must be in YYYY-MM-DD format";
    if (!isDateFieldEmpty(form.date_feature_from) && !isValidDateValue(form.date_feature_from))
      errors.date_feature_from =
        "Date Feature From must be in YYYY-MM-DD format";
    if (!isDateFieldEmpty(form.date_feature_to) && !isValidDateValue(form.date_feature_to))
      errors.date_feature_to = "Date Feature To must be in YYYY-MM-DD format";
    // Conditional required: if date_taken_back is filled, require date_taken_on and date_live
    if (form.date_taken_back) {
      if (!form.date_taken_on)
        errors.date_taken_on =
          "Date Taken On is required if Date Taken Back is filled";
      if (!form.date_live)
        errors.date_live = "Date Live is required if Date Taken Back is filled";
    }
    // Conditional required: if vip_rule_type is '2', require at least one VIP rule with amount
    if (form.vip_rule_type === "2" || form.vip_rule_type === 2) {
      if (vipRules.length === 0) {
        errors.vip_amount_rule = "At least one VIP rule is required when VIP Rule Type is Unique";
      } else {
        const hasInvalidRule = vipRules.some(rule => !rule.amount || rule.amount.trim() === "");
        if (hasInvalidRule) {
          errors.vip_amount_rule = "All VIP rules must have an amount";
        }
      }
    }
    // Liability: skip for now (complex structure)
    return errors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log("🔄 Form submission started");
    setError("");
    console.log("📝 Current form data:", form);

    // Check specific required fields
    console.log("🔍 Required field values:");
    console.log("  - name:", form.name);
    console.log("  - property_id:", form.property_id);
    console.log("  - status_manual:", form.status_manual);
    console.log("  - client_id:", form.client_id);
    console.log("  - price_from:", form.price_from);
    console.log("  - price_to:", form.price_to);
    console.log("  - actual_market_rent:", form.actual_market_rent);
    console.log("  - spaces:", form.spaces);
    console.log("  - bathrooms:", form.bathrooms);
    console.log("  - kitchens:", form.kitchens);

    const errors = validateForm();
    console.log("🔍 Validation errors:", errors);
    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) {
      console.log("❌ Form validation failed, preventing submission");
      return;
    }

    // Wait for file uploads to complete
    if (uploadingFiles) {
      setError("Please wait for file uploads to complete before saving");
      return;
    }

    // Prepare form data for submission
    const formData = { ...form };
    console.log("📋 Original form data for submission:", formData);

    // Remove invalid fields that don't exist in the backend
    const validFields = [
      "name",
      "property_id",
      "status",
      "status_manual",
      "residential_commercial",
      "client_id",
      "client_contact_id",
      "client_type",
      "building_manager_id",
      "main_photo_key",
      "price_from",
      "price_to",
      "actual_market_rent",
      "square_footage",
      "rateable_value",
      "rebuild_value",
      "location_id",
      "type_id",
      "spaces",
      "bathrooms",
      "kitchens",
      "short_description",
      "public_description",
      "public_short_description",
      "website_display_name",
      "address_1",
      "address_2",
      "address_3",
      "town",
      "postcode",
      "garden",
      "parking",
      "vip_rule_type",
      "vip_amount_rule",
      "date_taken_on",
      "date_live",
      "date_taken_back",
      "date_feature_from",
      "date_feature_to",
      "fit_out_notes",
      "utilities_notes",
      "geo_latitude",
      "geo_longitude",
      "photo_keys",
      "video",
      "3d_mapping",
    ];

    // Filter out invalid fields and map field names
    const cleanedFormData = {};
    const removedFields = [];
    // First, explicitly include all description fields to ensure they're saved
    // This fixes the issue where description boxes weren't being saved
    const descriptionFields = [
      "short_description",
      "public_description",
      "public_short_description",
      "fit_out_notes",
      "utilities_notes"
    ];
    
    descriptionFields.forEach((field) => {
      if (form[field] !== undefined) {
        cleanedFormData[field] = form[field] || "";
      }
    });
    
    // Then process other fields, but skip description (it's already handled above via public_description)
    Object.keys(form).forEach((key) => {
      // Skip description field - it's handled separately above via public_description
      // Skip description fields that are already handled above
      if (key === "description" || descriptionFields.includes(key)) {
        return;
      }
      
      // Map field names if needed
      let mappedKey = key;
      if (key === "mapping") {
        mappedKey = "3d_mapping";
      }

      if (validFields.includes(mappedKey)) {
        cleanedFormData[mappedKey] = form[key];
      } else {
        removedFields.push(key);
      }
    });
    
    // Convert file fields to S3 keys if they exist
    if (
      cleanedFormData.main_photo &&
      typeof cleanedFormData.main_photo === "string"
    ) {
      cleanedFormData.main_photo_key = cleanedFormData.main_photo;
      delete cleanedFormData.main_photo;
    }

    if (cleanedFormData.photos && Array.isArray(cleanedFormData.photos)) {
      cleanedFormData.photo_keys = cleanedFormData.photos.filter(
        (key) => typeof key === "string"
      );
      delete cleanedFormData.photos;
    }

    // Normalize Garden/Parking to 0/1 expected by backend (Laravel defaults to 0 = "No")
    if (cleanedFormData.garden === "Yes") {
      cleanedFormData.garden = "1";
    } else if (cleanedFormData.garden === "No") {
      cleanedFormData.garden = "0";
    } else if (cleanedFormData.garden === "" || cleanedFormData.garden === null || cleanedFormData.garden === undefined) {
      cleanedFormData.garden = "0"; // Default to "No" if empty
    }

    if (cleanedFormData.parking === "Yes") {
      cleanedFormData.parking = "1";
    } else if (cleanedFormData.parking === "No") {
      cleanedFormData.parking = "0";
    } else if (cleanedFormData.parking === "" || cleanedFormData.parking === null || cleanedFormData.parking === undefined) {
      cleanedFormData.parking = "0"; // Default to "No" if empty
    }

    console.log("🧹 Cleaned form data:", cleanedFormData);
    console.log("🗑️ Removed fields:", removedFields);

    // Convert YYYY-MM-DD to DD/MM/YYYY for backend formatter
    const toDdMmYyyy = (iso) =>
      iso && /^\d{4}-\d{2}-\d{2}$/.test(iso)
        ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`
        : iso;
    [
      "date_taken_on",
      "date_live",
      "date_taken_back",
      "date_feature_from",
      "date_feature_to",
    ].forEach((field) => {
      if (cleanedFormData[field]) {
        cleanedFormData[field] = toDdMmYyyy(cleanedFormData[field]);
      }
    });

    // Ensure backend receives numeric status consistent with status_manual selection
    // IMPORTANT: When status_manual = 999 (Automatic), DO NOT set status
    // The status column is TINYINT (0-255 range), so 999 is invalid
    // For Automatic status, status is calculated dynamically based on room occupancy, not stored
    if (cleanedFormData.status_manual && cleanedFormData.status_manual !== 999 && cleanedFormData.status_manual !== "999") {
      cleanedFormData.status = parseInt(cleanedFormData.status_manual, 10);
    } else if (cleanedFormData.status_manual === 999 || cleanedFormData.status_manual === "999") {
      // Remove status field when status_manual = 999 (Automatic)
      if (cleanedFormData.status !== undefined) {
        removedFields.push("status");
        delete cleanedFormData.status;
        console.log("🗑️ Removed status field because status_manual = 999 (Automatic)");
      }
    }

    // Handle VIP rule type - convert empty string to null, or convert to integer
    if (cleanedFormData.vip_rule_type === "" || cleanedFormData.vip_rule_type === null || cleanedFormData.vip_rule_type === undefined) {
      cleanedFormData.vip_rule_type = null;
    } else {
      // Convert to integer if it's a valid value
      const vipRuleTypeInt = parseInt(cleanedFormData.vip_rule_type, 10);
      if (!isNaN(vipRuleTypeInt)) {
        cleanedFormData.vip_rule_type = vipRuleTypeInt;
      } else {
        cleanedFormData.vip_rule_type = null;
      }
    }

    // Handle integer fields - convert empty strings to null, or convert to integer
    const integerFields = [
      "client_type",
      "client_contact_id",
      "building_manager_id",
      "location_id",
      "type_id",
      "block_id",
      "inspection_route_id"
    ];

    integerFields.forEach((field) => {
      if (cleanedFormData[field] === "" || cleanedFormData[field] === null || cleanedFormData[field] === undefined) {
        cleanedFormData[field] = null;
      } else {
        // Convert to integer if it's a valid value
        const intValue = parseInt(cleanedFormData[field], 10);
        if (!isNaN(intValue)) {
          cleanedFormData[field] = intValue;
        } else {
          cleanedFormData[field] = null;
        }
      }
    });

    // Serialize VIP rules to JSON if VIP rule type is unique (2)
    if (cleanedFormData.vip_rule_type === 2) {
      if (vipRules.length > 0) {
        // Filter out empty rules and serialize to JSON
        const validRules = vipRules.filter(rule => rule.amount && rule.amount.trim() !== "");
        cleanedFormData.vip_amount_rule = JSON.stringify(validRules);
      } else {
        cleanedFormData.vip_amount_rule = null;
      }
    } else {
      // For non-unique VIP rules, set vip_amount_rule to null
      cleanedFormData.vip_amount_rule = null;
    }

    console.log("🚀 Making API call to update property");
    console.log("📤 Request payload:", cleanedFormData);
    console.log(
      "🔗 API URL:",
      `${process.env.REACT_APP_API_URL}/api/properties/${id}`
    );

    fetch(`${process.env.REACT_APP_API_URL}/api/properties/${id}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
      body: JSON.stringify(cleanedFormData),
    })
      .then((res) => {
        if (res.ok) {
          return res.json();
        } else {
          return res.json().then((err) => {
            console.error("Update API error response:", err);
            // Handle validation errors from backend
            if (err.errors && typeof err.errors === 'object') {
              // Convert backend error format (arrays) to frontend format (strings)
              const backendErrors = {};
              Object.keys(err.errors).forEach(field => {
                const errorValue = err.errors[field];
                backendErrors[field] = Array.isArray(errorValue) ? errorValue[0] : errorValue;
              });
              setValidationErrors(backendErrors);
              // Don't throw generic error when we have specific field errors
              // The field-specific errors are already displayed inline
              return { success: false, hasFieldErrors: true };
            }
            throw new Error(err.message || "Failed to update property");
          });
        }
      })
      .then((data) => {
        console.log("✅ API response received:", data);
        if (data.success) {
          console.log("🎉 Property updated successfully, navigating...");
          navigate(`/properties/${id}`);
        } else if (data.hasFieldErrors) {
          // Field-specific validation errors are already displayed inline
          // Don't show generic error message at top
          console.log("⚠️ Validation errors displayed inline");
        } else {
          console.log("❌ API returned success: false");
          throw new Error(data.message || "Failed to update property");
        }
      })
      .catch((err) => {
        console.error("💥 Error updating property:", err);
        setError(err.message || "Failed to update property");
      });
  };

  if (loading) return <Loader />;

  // Laravel equivalent permission check - properties.edit required
  if (!hasPermission('properties.edit')) {
    console.log('🔐 [PropertyEdit] Access denied - missing properties.edit permission');
    toast.error("You don't have permission to edit properties!");
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="container" style={{ maxWidth: 1100, margin: "0 auto" }}>
      <div className="breadcrumb-row">
        <span className="breadcrumb-main" onClick={() => navigate('/dashboard')} style={{ cursor: 'pointer', color: '#337ab7' }}>Dashboard</span>
        <span className="breadcrumb-sep">/</span>
        <span className="breadcrumb-properties" onClick={() => navigate('/properties')} style={{ cursor: 'pointer', color: '#337ab7' }}>Properties</span>
        <span className="breadcrumb-sep">/</span>
        <span className="breadcrumb-edit">Edit Property</span>
      </div>
      <form
        className="panel panel-default panel-form"
        onSubmit={(e) => {
          console.log("📝 Form onSubmit triggered");
          handleSubmit(e);
        }}
        style={{ margin: "0 auto", marginTop: 24, border: "none" }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div className="panel-heading panel-title">
            <h3>Edit Property</h3>
          </div>
          <button
            onClick={() => navigate(`/properties/${id}/photos`)}
            id="update_property_button"
            className="btn btn-head btn-standard "
          >
            Photos
          </button>
        </div>
        <div
          className="panel-body"
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 32,
            border: "none",
          }}
        >
          {error && <div className="alert alert-danger">{error}</div>}

          {/* Debug Info */}

          {/* General Section */}
          <div className=" ">
            <div
              className="panel-body"
              style={{
                display: "flex",
                flexDirection: "column",
                border: "none",
              }}
            >
              <div className="form-group create-property-input-field">
                <label>
                  Name <span>*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  className={`form-control${
                    validationErrors.name ? " is-invalid" : ""
                  }`}
                  value={form.name ?? ""}
                  onChange={handleChange}
                  required
                />
                {validationErrors.name && (
                  <div
                    className="invalid-feedback"
                    style={{ color: "red", fontSize: 13 }}
                  >
                    {validationErrors.name}
                  </div>
                )}
              </div>
              <div className="form-group create-property-input-field-id">
                <label>
                  Property ID <span style={{ color: "red" }}>*</span>
                </label>
                <input
                  type="text"
                  name="property_id"
                  className={`form-control${
                    validationErrors.property_id ? " is-invalid" : ""
                  }`}
                  value={form.property_id ?? ""}
                  onChange={handleChange}
                />
                {validationErrors.property_id && (
                  <div
                    className="invalid-feedback"
                    style={{ color: "red", fontSize: 13 }}
                  >
                    {validationErrors.property_id}
                  </div>
                )}
                <p>System suggests the lowest available number.</p>
              </div>
              <div className="form-group create-property-input-field-Status">
                <label>
                  Status <span style={{ color: "red" }}>*</span>
                </label>
                {console.log(
                  "🔍 Rendering status dropdown with formData:",
                  formData
                )}
                {console.log("🔍 manualStatuses:", formData.manualStatuses)}
                {console.log(
                  "🔍 manualStatuses type:",
                  typeof formData.manualStatuses
                )}
                {console.log(
                  "🔍 manualStatuses keys:",
                  Object.keys(formData.manualStatuses || {})
                )}
                <select
                  name="status_manual"
                  className={`form-control${
                    validationErrors.status_manual ? " is-invalid" : ""
                  }`}
                  value={form.status_manual ? String(form.status_manual) : ""}
                  onChange={handleChange}
                  required
                >
                  <option value="">--- Select Status ---</option>
                  {Object.entries(formData.manualStatuses || {}).map(
                    ([key, value]) => (
                      <option key={key} value={String(key)}>
                        {value}
                      </option>
                    )
                  )}
                  {/* Fallback options if formData.manualStatuses is not loaded */}
                  {(!formData.manualStatuses ||
                    Object.keys(formData.manualStatuses).length === 0) && (
                    <>
                      <option value="999">Automatic</option>
                      <option value="9">Pre-Offer</option>
                      <option value="0">Under Offer</option>
                      <option value="4">Signed Proposal</option>
                      <option value="5">Maintenance</option>
                      <option value="6">Rejected Offer</option>
                      <option value="11">Key Holding</option>
                      <option value="14">Caretakers</option>
                    </>
                  )}
                </select>
                {validationErrors.status_manual && (
                  <div
                    className="invalid-feedback"
                    style={{ color: "red", fontSize: 13 }}
                  >
                    {validationErrors.status_manual}
                  </div>
                )}
              </div>
              <div
                className="form-group create-property-input-field-text"
                style={{ width: "444px", margin: "auto" }}
              >
                <p>
                  When <span>Automatic</span> the system will try to set: Under
                  Offer, Empty, Not Full, Full, Handing Back or Ended
                </p>
              </div>
              {/* Residential/Commercial — required, and positioned between
                  Status and Client as the client's Sept 2026 layout asks.
                  Drives the Building Set Up KPI deadline: 14 calendar days for
                  a residential property, 28 for a commercial one. */}
              <div className="form-group create-property-input-field-Status">
                <label htmlFor="residential_commercial">
                  Residential/Commercial <span style={{ color: "red" }}>*</span>
                </label>
                <select
                  id="residential_commercial"
                  name="residential_commercial"
                  className={`form-control${
                    validationErrors.residential_commercial ? " is-invalid" : ""
                  }`}
                  value={form.residential_commercial ?? ""}
                  onChange={handleChange}
                >
                  <option value="">--- Select ---</option>
                  <option value="residential">Residential</option>
                  <option value="commercial">Commercial</option>
                </select>
                {validationErrors.residential_commercial && (
                  <div
                    className="invalid-feedback"
                    style={{ color: "red", fontSize: 13 }}
                  >
                    {validationErrors.residential_commercial}
                  </div>
                )}
              </div>
              <div className="form-group create-property-input-field-Status">
                <label>
                  Client <span style={{ color: "red" }}>*</span>
                </label>
                <select
                  name="client_id"
                  className={`form-control${
                    validationErrors.client_id ? " is-invalid" : ""
                  }`}
                  value={form.client_id ?? ""}
                  onChange={handleChange}
                >
                  <option value="">--- Select Client ---</option>
                  {Object.entries(formData.clients || {}).map(
                    ([key, value]) => (
                      <option key={key} value={key}>
                        {value}
                      </option>
                    )
                  )}
                </select>
                {validationErrors.client_id && (
                  <div
                    className="invalid-feedback"
                    style={{ color: "red", fontSize: 13 }}
                  >
                    {validationErrors.client_id}
                  </div>
                )}
              </div>
              <div className="form-group create-property-input-field-Status">
                <label>Client Contact</label>
                <select
                  name="client_contact_id"
                  className="form-control"
                  value={form.client_contact_id ?? ""}
                  onChange={handleChange}
                  disabled={!form.client_id}
                >
                  <option value="">--- Select Client Contact ---</option>
                  {Array.isArray(clientContacts) &&
                    clientContacts.length > 0 &&
                    clientContacts.map((contact) => (
                      <option key={contact.id} value={contact.id}>
                        {contact.name || contact.display_name || contact.full_name || `Contact ${contact.id}`}
                      </option>
                    ))}
                  {Array.isArray(clientContacts) && clientContacts.length === 0 && form.client_id && (
                    <option value="" disabled>No contacts found for this client</option>
                  )}
                </select>
                {!form.client_id && (
                  <small style={{ color: "#666", fontSize: "12px", display: "block", marginTop: "4px" }}>
                    Please select a client first
                  </small>
                )}
              </div>
              <div className="form-group create-property-input-field-Status">
                <label>Client Type</label>
                <select
                  name="client_type"
                  className="form-control"
                  value={form.client_type ?? ""}
                  onChange={handleChange}
                >
                  <option value="">--- Select Client Type ---</option>
                  {Object.entries(formData.clientTypes || {}).map(
                    ([key, value]) => (
                      <option key={key} value={key}>
                        {value}
                      </option>
                    )
                  )}
                </select>
              </div>
              <div className="form-group create-property-input-field-Status">
                <label>Building Manager</label>
                <select
                  name="building_manager_id"
                  className={`form-control${
                    validationErrors.building_manager_id ? " is-invalid" : ""
                  }`}
                  value={form.building_manager_id ?? ""}
                  onChange={handleChange}
                >
                  <option value="">--- Select Building Manager ---</option>
                  {Object.entries(formData.buildingManagers || {}).map(
                    ([key, value]) => (
                      <option key={key} value={key}>
                        {value}
                      </option>
                    )
                  )}
                </select>
                {validationErrors.building_manager_id && (
                  <div
                    className="invalid-feedback"
                    style={{ color: "red", fontSize: 13 }}
                  >
                    {validationErrors.building_manager_id}
                  </div>
                )}
              </div>
              <div className="form-group create-property-input-field">
                <label>Main Photo</label>
                <input
                  type="file"
                  name="main_photo"
                  className="form-control"
                  onChange={handleChange}
                  accept="image/*"
                />
                {form.main_photo && (
                  <div style={{ marginTop: "10px", marginLeft: "29%" }}>
                    <img
                      src={getS3Url(form.main_photo)}
                      alt="Main photo"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "/images/default-property.jpg";
                      }}
                      style={{
                        maxWidth: "200px",
                        maxHeight: "150px",
                        objectFit: "cover",
                        border: "1px solid #ddd",
                        borderRadius: "4px",
                      }}
                    />
                    <button
                      type="button"
                      onClick={removeMainPhoto}
                      style={{
                        marginLeft: "10px",
                        padding: "5px 10px",
                        backgroundColor: "#dc3545",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        cursor: "pointer",
                      }}
                    >
                      Remove
                    </button>
                  </div>
                )}
                {uploadingFiles && (
                  <div style={{ marginTop: "10px", color: "#007bff" }}>
                    Uploading...
                  </div>
                )}
              </div>
              <div
                className="form-group create-property-input-field-text"
                style={{ width: "444px", margin: "auto", marginTop: "-12px" }}
              >
                <p>
                  The file can not be larger than 100MB. If you don't want to
                  change file - just leave the field empty.
                </p>
              </div>
              <div className="form-group create-property-input-number">
                <label>
                  Price From <span style={{ color: "red" }}>*</span>
                </label>
                <input
                  type="number"
                  name="price_from"
                  className={`form-control${
                    validationErrors.price_from ? " is-invalid" : ""
                  }`}
                  value={form.price_from ?? ""}
                  onChange={handleChange}
                />
                {validationErrors.price_from && (
                  <div
                    className="invalid-feedback"
                    style={{ color: "red", fontSize: 13 }}
                  >
                    {validationErrors.price_from}
                  </div>
                )}
              </div>
              <div className="form-group create-property-input-number">
                <label>
                  Price To <span style={{ color: "red" }}>*</span>
                </label>
                <input
                  type="number"
                  name="price_to"
                  className={`form-control${
                    validationErrors.price_to ? " is-invalid" : ""
                  }`}
                  value={form.price_to ?? ""}
                  onChange={handleChange}
                />
                {validationErrors.price_to && (
                  <div
                    className="invalid-feedback"
                    style={{ color: "red", fontSize: 13 }}
                  >
                    {validationErrors.price_to}
                  </div>
                )}
              </div>
              <div className="form-group create-property-input-number">
                <label>
                  Actual Market Rent <span style={{ color: "red" }}>*</span>
                </label>
                <input
                  type="number"
                  name="actual_market_rent"
                  className={`form-control${
                    validationErrors.actual_market_rent ? " is-invalid" : ""
                  }`}
                  value={form.actual_market_rent ?? ""}
                  onChange={handleChange}
                />
                {validationErrors.actual_market_rent && (
                  <div
                    className="invalid-feedback"
                    style={{ color: "red", fontSize: 13 }}
                  >
                    {validationErrors.actual_market_rent}
                  </div>
                )}
              </div>
              <div className="form-group create-property-input-number">
                <label>
                  Square Footage
                </label>
                <input
                  type="number"
                  name="square_footage"
                  className={`form-control${
                    validationErrors.square_footage ? " is-invalid" : ""
                  }`}
                  value={form.square_footage ?? ""}
                  onChange={handleChange}
                />
                {validationErrors.square_footage && (
                  <div
                    className="invalid-feedback"
                    style={{ color: "red", fontSize: 13 }}
                  >
                    {validationErrors.square_footage}
                  </div>
                )}
              </div>
              <div className="form-group create-property-input-number">
                <label>Rateable Value</label>
                <input
                  type="number"
                  name="rateable_value"
                  className="form-control"
                  value={form.rateable_value}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group create-property-input-number">
                <label>Rebuild Value</label>
                <input
                  type="number"
                  name="rebuild_value"
                  className="form-control"
                  value={form.rebuild_value}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group create-property-input-field-Status">
                <label>
                  Location <span style={{ color: "red" }}>*</span>
                </label>
                <select
                  name="location_id"
                  className={`form-control${
                    validationErrors.location_id ? " is-invalid" : ""
                  }`}
                  value={form.location_id ?? ""}
                  onChange={handleChange}
                >
                  <option value="">--- Select Location ---</option>
                  {Object.entries(formData.locations || {}).map(
                    ([key, value]) => (
                      <option key={key} value={key}>
                        {value}
                      </option>
                    )
                  )}
                </select>
                {validationErrors.location_id && (
                  <div
                    className="invalid-feedback"
                    style={{ color: "red", fontSize: 13 }}
                  >
                    {validationErrors.location_id}
                  </div>
                )}
              </div>
              <div className="form-group create-property-input-field-Status">
                <label>
                  Type <span style={{ color: "red" }}>*</span>
                </label>
                <select
                  name="type_id"
                  className={`form-control${
                    validationErrors.type_id ? " is-invalid" : ""
                  }`}
                  value={form.type_id ?? ""}
                  onChange={handleChange}
                >
                  <option value="">--- Select Type ---</option>
                  {Object.entries(formData.types || {}).map(([key, value]) => (
                    <option key={key} value={key}>
                      {value}
                    </option>
                  ))}
                </select>
                {validationErrors.type_id && (
                  <div
                    className="invalid-feedback"
                    style={{ color: "red", fontSize: 13 }}
                  >
                    {validationErrors.type_id}
                  </div>
                )}
              </div>
              <div className="form-group create-property-input-number">
                <label>
                  Spaces <span style={{ color: "red" }}>*</span>
                </label>
                <input
                  type="number"
                  name="spaces"
                  className={`form-control${
                    validationErrors.spaces ? " is-invalid" : ""
                  }`}
                  value={form.spaces ?? ""}
                  onChange={handleChange}
                />
                {validationErrors.spaces && (
                  <div
                    className="invalid-feedback"
                    style={{ color: "red", fontSize: 13 }}
                  >
                    {validationErrors.spaces}
                  </div>
                )}
              </div>
              <div className="form-group create-property-input-number">
                <label>
                  Bathrooms <span style={{ color: "red" }}>*</span>
                </label>
                <input
                  type="number"
                  name="bathrooms"
                  className={`form-control${
                    validationErrors.bathrooms ? " is-invalid" : ""
                  }`}
                  value={form.bathrooms ?? ""}
                  onChange={handleChange}
                />
                {validationErrors.bathrooms && (
                  <div
                    className="invalid-feedback"
                    style={{ color: "red", fontSize: 13 }}
                  >
                    {validationErrors.bathrooms}
                  </div>
                )}
              </div>
              <div className="form-group create-property-input-number">
                <label>
                  kitchens (number) <span>*</span>
                </label>
                <input
                  type="number"
                  name="kitchens"
                  className={`form-control${
                    validationErrors.kitchens ? " is-invalid" : ""
                  }`}
                  value={form.kitchens ?? ""}
                  onChange={handleChange}
                  required
                />
                {validationErrors.kitchens && (
                  <div
                    className="invalid-feedback"
                    style={{ color: "red", fontSize: 13 }}
                  >
                    {validationErrors.kitchens}
                  </div>
                )}
              </div>
              <div className="form-group create-property-input-field-Status">
                <label>
                  Garden
                </label>
                <select
                  style={{ width: "200px" }}
                  name="garden"
                  className="form-control"
                  value={form.garden || "No"}
                  onChange={handleChange}
                >
                  <option value="No">No</option>
                  <option value="Yes">Yes</option>
                </select>
              </div>
              <div className="form-group create-property-input-field-Status">
                <label>
                  Parking
                </label>
                <select
                  style={{ width: "200px" }}
                  name="parking"
                  className="form-control"
                  value={form.parking || "No"}
                  onChange={handleChange}
                >
                  <option value="No">No</option>
                  <option value="Yes">Yes</option>
                </select>
              </div>
              <div className="form-group create-property-input-field-radio">
                <label>VIP provided rule </label>
                <small
                  style={{
                    display: "block",
                    marginLeft: "32px",
                    color: "#666",
                    fontStyle: "italic",
                  }}
                >
                  Tip: click a selected option again to clear it.
                </small>
                <div
                  style={{ marginLeft: "32px", display: "flex", gap: "32px" }}
                >
                  <div style={{ width: "190px" }}>
                    <input
                      onChange={handleChange}
                      onClick={() => handleVipRuleTypeClick("0")}
                      type="radio"
                      name="vip_rule_type"
                      value="0"
                      checked={form.vip_rule_type === "0" || form.vip_rule_type === 0}
                    />
                    <p>£ 100 - 1st month after 16th</p>
                    <p>£ 200 - 2nd-6th months</p>
                    <p>£ 250 - Onwards</p>
                  </div>
                  <div style={{ width: "190px" }}>
                    <input
                      onChange={handleChange}
                      onClick={() => handleVipRuleTypeClick("1")}
                      name="vip_rule_type"
                      type="radio"
                      value="1"
                      checked={form.vip_rule_type === "1" || form.vip_rule_type === 1}
                    />
                    <p>£ 110 - 1st month after 16th</p>
                    <p>£ 220 - 2nd-6th months</p>
                    <p>£ 275 - Onwards</p>
                  </div>
                  <div style={{ width: "190px" }}>
                    <input
                      style={{ marginLeft: "32px" }}
                      onChange={handleChange}
                      name="vip_rule_type"
                      onClick={() => handleVipRuleTypeClick("2")}
                      type="radio"
                      value="2"
                      checked={form.vip_rule_type === "2" || form.vip_rule_type === 2}
                    />
                    <div style={{ textAlign: "center" }}>
                      <p>Unique vip rule</p>
                      <button
                        type="button"
                        onClick={handleAddVipRule}
                        style={{
                          border: "none",
                          background: "transparent",
                          color: "green",
                          fontWeight: "bold",
                          cursor: "pointer",
                          padding: 0,
                          textDecoration: "underline"
                        }}
                      >
                        Add rule
                      </button>
                    </div>
                  </div>
                </div>
                {/* Dynamic VIP Rules Builder */}
                {(form.vip_rule_type === "2" || form.vip_rule_type === 2) && (
                  <div style={{ marginTop: "20px", marginLeft: "32px" }}>
                    {vipRules.length === 0 && (
                      <p style={{ color: "#666", fontStyle: "italic", marginBottom: "10px" }}>
                        Click "Add rule" above to create custom VIP rules
                      </p>
                    )}
                    {vipRules.map((rule, index) => (
                      <div
                        key={index}
                        style={{
                          display: "flex",
                          gap: "10px",
                          marginBottom: "10px",
                          alignItems: "flex-end"
                        }}
                      >
                        <div style={{ width: "200px" }}>
                          <label style={{ display: "block", marginBottom: "5px" }}>
                            For (months)
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            value={rule.months || ""}
                            onChange={(e) =>
                              handleVipRuleChange(index, "months", e.target.value)
                            }
                            placeholder="Onwards"
                          />
                        </div>
                        <div style={{ width: "200px" }}>
                          <label style={{ display: "block", marginBottom: "5px" }}>
                            Amount
                          </label>
                          <input
                            type="text"
                            value={rule.amount || ""}
                            onChange={(e) =>
                              handleVipRuleChange(index, "amount", e.target.value)
                            }
                            placeholder="Enter amount"
                            className={`form-control${
                              validationErrors.vip_amount_rule &&
                              (!rule.amount || rule.amount.trim() === "")
                                ? " is-invalid"
                                : ""
                            }`}
                          />
                        </div>
                        <button
                          type="button"
                          onClick={handleAddVipRule}
                          style={{
                            border: "none",
                            background: "transparent",
                            color: "green",
                            fontWeight: "bold",
                            cursor: "pointer",
                            height: "34px"
                          }}
                        >
                          Add
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRemoveVipRule(index)}
                          style={{
                            border: "none",
                            background: "transparent",
                            color: "red",
                            fontWeight: "bold",
                            cursor: "pointer",
                            height: "34px"
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    ))}
                    {validationErrors.vip_amount_rule && (
                      <div
                        className="invalid-feedback"
                        style={{ display: "block", marginTop: "5px" }}
                      >
                        {validationErrors.vip_amount_rule}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="form-group create-property-input-field">
                <label>
                  Total VIP Paid
                </label>
                <input
                  placeholder="Enter total VIP paid"
                  type="number"
                  name="total_vip_paid"
                  className={"form-control"}
                  value={form.total_vip_paid}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group create-property-input-field">
                <label>Date Taken On</label>
                <DatePicker
                  selected={parseDate(form.date_taken_on)}
                  onChange={(date) => setForm({ ...form, date_taken_on: formatDateForBackend(date) })}
                  dateFormat="dd/MM/yyyy"
                  className={`form-control${validationErrors.date_taken_on ? " is-invalid" : ""}`}
                  placeholderText="dd/mm/yyyy"
                  isClearable
                  showYearDropdown
                  scrollableYearDropdown
                  yearDropdownItemNumber={50}
                />
                {validationErrors.date_taken_on && (
                  <div
                    className="invalid-feedback"
                    style={{ color: "red", fontSize: 13, display: "block" }}
                  >
                    {validationErrors.date_taken_on}
                  </div>
                )}
              </div>
              <div className="form-group create-property-input-field">
                <label>Date Live</label>
                <DatePicker
                  selected={parseDate(form.date_live)}
                  onChange={(date) => setForm({ ...form, date_live: formatDateForBackend(date) })}
                  dateFormat="dd/MM/yyyy"
                  className={`form-control${validationErrors.date_live ? " is-invalid" : ""}`}
                  placeholderText="dd/mm/yyyy"
                  isClearable
                  showYearDropdown
                  scrollableYearDropdown
                  yearDropdownItemNumber={50}
                />
                {validationErrors.date_live && (
                  <div
                    className="invalid-feedback"
                    style={{ color: "red", fontSize: 13, display: "block" }}
                  >
                    {validationErrors.date_live}
                  </div>
                )}
              </div>
              <div className="form-group create-property-input-field">
                <label>Date Taken Back</label>
                <DatePicker
                  selected={parseDate(form.date_taken_back)}
                  onChange={(date) => setForm({ ...form, date_taken_back: formatDateForBackend(date) })}
                  dateFormat="dd/MM/yyyy"
                  className={`form-control${validationErrors.date_taken_back ? " is-invalid" : ""}`}
                  placeholderText="dd/mm/yyyy"
                  isClearable
                  showYearDropdown
                  scrollableYearDropdown
                  yearDropdownItemNumber={50}
                />
                {validationErrors.date_taken_back && (
                  <div
                    className="invalid-feedback"
                    style={{ color: "red", fontSize: 13, display: "block" }}
                  >
                    {validationErrors.date_taken_back}
                  </div>
                )}
              </div>
              <div className="form-group create-property-input-field">
                <label>Today</label>
                <input
                  type="text"
                  name="today_date"
                  className="form-control"
                  value={format(new Date(), "dd/MM/yyyy")}
                  readOnly
                  disabled
                  style={{ backgroundColor: "#f5f5f5", cursor: "not-allowed" }}
                />
              </div>
              <div className="form-group create-property-input-field">
                <label>Date Feature From</label>
                <DatePicker
                  selected={parseDate(form.date_feature_from)}
                  onChange={(date) => setForm({ ...form, date_feature_from: formatDateForBackend(date) })}
                  dateFormat="dd/MM/yyyy"
                  className="form-control"
                  placeholderText="dd/mm/yyyy"
                  isClearable
                  showYearDropdown
                  scrollableYearDropdown
                  yearDropdownItemNumber={50}
                />
              </div>
              <div className="form-group create-property-input-field">
                <label>Date Feature To</label>
                <DatePicker
                  selected={parseDate(form.date_feature_to)}
                  onChange={(date) => setForm({ ...form, date_feature_to: formatDateForBackend(date) })}
                  dateFormat="dd/MM/yyyy"
                  className="form-control"
                  placeholderText="dd/mm/yyyy"
                  isClearable
                  showYearDropdown
                  scrollableYearDropdown
                  yearDropdownItemNumber={50}
                />
              </div>
              <div className="form-group create-property-input-field">
                <label>Video</label>
                <input
                  type="text"
                  name="video"
                  className={`form-control${validationErrors.video ? " is-invalid" : ""}`}
                  value={form.video}
                  onChange={handleChange}
                />
                {validationErrors.video && (
                  <div className="invalid-feedback">{validationErrors.video}</div>
                )}
              </div>
              <div className="form-group create-property-input-field">
                <label>3D Mapping</label>
                <input
                  type="text"
                  name="mapping"
                  className="form-control"
                  value={form.mapping}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group create-property-input-field">
                <label>Address 1</label>
                <input
                  type="text"
                  name="address_1"
                  className="form-control"
                  value={form.address_1}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group create-property-input-field">
                <label>Address 2</label>
                <input
                  type="text"
                  name="address_2"
                  className="form-control"
                  value={form.address_2}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group create-property-input-field">
                <label>Address 3</label>
                <input
                  type="text"
                  name="address_3"
                  className="form-control"
                  value={form.address_3}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group create-property-input-field">
                <label>Town</label>
                <input
                  type="text"
                  name="town"
                  className="form-control"
                  value={form.town}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group create-property-input-field">
                <label>Postcode</label>
                <input
                  type="text"
                  name="postcode"
                  className="form-control"
                  value={form.postcode}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group" style={{ marginBottom: "20px" }}>
                <div style={{ marginLeft: "29%", width: "75%" }}>
                  <button
                    type="button"
                    className="btn btn-head btn-standard"
                    onClick={handleGenerateGeoCode}
                    disabled={!form.postcode || generatingGeoCode}
                    style={{
                      marginBottom: "15px",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      backgroundColor: "#17264B",
                      borderColor: "#17264B"
                    }}
                  >
                    Generate geo codes from the postcode
                    {generatingGeoCode && (
                      <div
                        style={{
                          width: "16px",
                          height: "16px",
                          border: "2px solid #ffffff",
                          borderTop: "2px solid transparent",
                          borderRadius: "50%",
                          animation: "spin 1s linear infinite",
                        }}
                      />
                    )}
                  </button>
                  <style>
                    {`
                      @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                      }
                    `}
                  </style>
                </div>
              </div>
              <div className="form-group create-property-input-field">
                <label>Geo Latitude</label>
                <input
                  type="text"
                  name="geo_latitude"
                  className="form-control"
                  value={form.geo_latitude}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group create-property-input-field">
                <label>Geo Longitude</label>
                <input
                  type="text"
                  name="geo_longitude"
                  className="form-control"
                  value={form.geo_longitude}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group create-property-textarea-field">
                <label>Property Notes</label>
                <textarea
                  type="text"
                  name="short_description"
                  className="form-control"
                  value={form.short_description}
                  onChange={handleChange}
                />
              </div>
              <div
                className="form-group create-property-input-field-text"
                style={{ width: "444px", margin: "auto", marginTop: "-12px" }}
              >
                <p>Internal description.</p>
              </div>
              <div className="form-group create-property-textarea-field">
                <label>Fit out notes</label>
                <textarea
                  type="text"
                  name="fit_out_notes"
                  className="form-control"
                  value={form.fit_out_notes}
                  onChange={handleChange}
                />
              </div>
              <div
                className="form-group create-property-input-field-text"
                style={{
                  width: "444px",
                  margin: "auto",
                  marginTop: "-12px",
                  fontWeight: 500,
                }}
              >
                <p>Fit out notes.</p>
              </div>
              <div className="form-group create-property-textarea-field">
                <label>Utilities notes</label>
                <textarea
                  type="text"
                  name="utilities_notes"
                  className="form-control"
                  value={form.utilities_notes}
                  onChange={handleChange}
                  rows={3}
                />
              </div>
              <div
                className="form-group create-property-input-field-text"
                style={{
                  width: "444px",
                  margin: "auto",
                  marginTop: "-12px",
                  fontWeight: 500,
                }}
              >
                <p>Utilities notes.</p>
              </div>
              <div className="form-group create-property-textarea-field">
                <label>Website Display Name</label>
                <input
                  type="text"
                  name="website_display_name"
                  className="form-control"
                  value={form.website_display_name}
                  onChange={handleChange}
                  placeholder="e.g. Edwards Drive, London — leave blank to use address"
                />
              </div>
              <div
                className="form-group create-property-input-field-text"
                style={{
                  width: "444px",
                  margin: "auto",
                  marginTop: "-12px",
                  fontWeight: 500,
                }}
              >
                <p>Custom name shown on the website. If empty, the address will be used.</p>
              </div>
              <div className="form-group create-property-textarea-field">
                <label>Short Description</label>
                <textarea
                  type="text"
                  name="public_short_description"
                  className="form-control"
                  value={form.public_short_description}
                  onChange={handleChange}
                />
              </div>
              <div
                className="form-group create-property-input-field-text"
                style={{
                  width: "444px",
                  margin: "auto",
                  marginTop: "-12px",
                  fontWeight: 500,
                }}
              >
                <p>Public short description.</p>
              </div>
              <div className="form-group create-property-textarea-field">
                <label>Description</label>
                <div style={{ width: "444px", marginLeft: "32px" }}>
                  <CKEditor
                    editor={ClassicEditor}
                    data={form.public_description || ""}
                    config={{
                    licenseKey: "GPL",
                    height: "200px",
                    toolbar: [
                      "heading",
                      "|",
                      "bold",
                      "italic",
                      "link",
                      "bulletedList",
                      "numberedList",
                      "|",
                      "outdent",
                      "indent",
                      "|",
                      "blockQuote",
                      "undo",
                      "redo",
                    ],
                    placeholder: "Enter public description here...",
                    removePlugins: [
                      "CKFinderUploadAdapter",
                      "CKFinder",
                      "EasyImage",
                      "Image",
                      "ImageCaption",
                      "ImageStyle",
                      "ImageToolbar",
                      "ImageUpload",
                    ],
                    }}
                    onChange={(event, editor) => {
                      const data = editor.getData();
                      setForm((prev) => ({ ...prev, public_description: data }));
                    }}
                  />
                </div>
              </div>
              <div
                className="form-group create-property-input-field-text"
                style={{
                  width: "444px",
                  margin: "auto",
                  marginTop: "-12px",
                  fontWeight: 500,
                }}
              >
                <p>Public description visible for guardians.</p>
              </div>
            </div>
          </div>
        </div>
        <div className="panel-footer text-center update-property-footer">
          <button
            type="submit"
            className="btn btn-head btn-standard"
            onClick={() => console.log("🔘 Save Changes button clicked")}
          >
            <i className="fa fa-save" style={{ marginRight: 6 }} /> Save Changes
          </button>
        </div>
      </form>
    </div>
  );
};

export default PropertyEdit;
